import instance from "lib/axiosInterceptor";
import {
  BookingsSetUpPayload,
  ConfirmBookingPayload,
  GetBookingsParams,
  GetBookingsResponse,
  GetMyBookingsParams,
  GetMyBookingsResponse,
  InviteToSpeakPayload,
  SpeakingInvitation,
  SpeakingInvitationStatus,
  GetSpeakingInvitationsParams,
  SpeakingInvitationsResponse,
  SendCorrespondence,
  GetCorrespondence,
  RespondToInvitation,
} from "interfaces";

/**
 * GET REQUESTS
 */

// Get all booking calendars
export async function getBookingCalendarsService(
  params: GetBookingsParams = {},
): Promise<GetBookingsResponse> {
  const normalizedParams: GetBookingsParams =
    params.startDate || params.endDate
      ? { ...params, range: 5 }
      : { ...params };

  Object.keys(normalizedParams).forEach((key) => {
    if (normalizedParams[key as keyof GetBookingsParams] === undefined) {
      delete normalizedParams[key as keyof GetBookingsParams];
    }
  });

  const response = await instance.get<GetBookingsResponse>(
    "/Bookings/Calendar",
    { params: normalizedParams },
  );
  return response.data;
}

// Get paginated "my bookings" list
export async function getMyBookingsService(
  params: GetMyBookingsParams = {},
): Promise<GetMyBookingsResponse> {
  const normalizedParams: GetMyBookingsParams =
    params.startDate || params.endDate
      ? {
          ...params,
          range: 5,
          startDate: params.startDate ?? "2020-01-01T00:00:00.000Z",
          endDate: params.endDate ?? new Date(new Date().setFullYear(new Date().getFullYear() + 100)).toISOString(),
        }
      : { ...params };

  Object.keys(normalizedParams).forEach((key) => {
    if (normalizedParams[key as keyof GetMyBookingsParams] === undefined) {
      delete normalizedParams[key as keyof GetMyBookingsParams];
    }
  });

  const response = await instance.get<GetMyBookingsResponse>(
    "/Bookings/Paginated",
    { params: normalizedParams },
  );
  return response.data;
}

/**
 * POST REQUESTS
 */

// Create a new booking calendar
export async function createBookingCalendarService(
  bookingData: BookingsSetUpPayload,
): Promise<string> {
  const response = await instance.post<string>(
    "/Bookings/Calendar",
    bookingData,
  );
  return response.data;
}

// Confirm a booking
export async function confirmBookingService(
  bookingId: string,
  payload: ConfirmBookingPayload,
): Promise<void> {
  await instance.post(`/Bookings/confirm/${bookingId}`, payload);
}

// cancel a booking slot
export async function cancelBookingSlotService(slotId: string): Promise<void> {
  await instance.put(`/Bookings/cancel/${slotId}`);
}

// Send meeting reminder
export async function sendMeetingReminderService(
  slotId: string,
): Promise<void> {
  await instance.post(`/Bookings/SendReminder/${slotId}`);
}

// Invite speaker to speak
export async function inviteToSpeakService(
  speakerId: string,
  payload: InviteToSpeakPayload,
): Promise<void> {
  await instance.post(`/Bookings/InviteToSpeak/${speakerId}`, payload);
}

export const getSpeakingInvitationService = async (
  params: GetSpeakingInvitationsParams = {},
): Promise<SpeakingInvitationsResponse> => {
  const result = await instance.get(`/Bookings/Invitations`, {
    params: {
      Page: params.page,
      PageLimit: params.pageLimit,
      Query: params.query,
      StartDate: params.startDate,
      EndDate: params.endDate,
      Status: params.status,
      SortDirection: params.sortDirection,
      Range: 5,
    },
  });

  const payload = result.data?.data ?? result.data;
  const listData = payload?.items ?? payload?.data ?? payload;
  const items: SpeakingInvitation[] = Array.isArray(listData)
    ? listData.map((item: SpeakingInvitation & { status: number | string }) => ({
        ...item,
        status: mapInvitationStatus(item.status),
      }))
    : [];

  return {
    items,
    pagination: payload?.pagination,
  };
};

function mapInvitationStatus(status: number | string): SpeakingInvitationStatus {
  if (status === 0 || status === "0" || status === "Pending") {
    return SpeakingInvitationStatus.Pending;
  }

  if (status === 1 || status === "1" || status === "Accepted") {
    return SpeakingInvitationStatus.Accepted;
  }

  if (status === 2 || status === "2" || status === "Declined") {
    return SpeakingInvitationStatus.Declined;
  }

  return SpeakingInvitationStatus.Pending;
}

export const getSpeakingCorrespondenceService = async (
  id: string,
): Promise<GetCorrespondence[]> => {
  const result = await instance.get(
    `/Bookings/Invitation/Correspondence/${id}`,
  );
  return result.data;
};

export const sendCorrespondenceService = async (
  payload: SendCorrespondence,
) => {
  const result = await instance.post(
    `/Bookings/Invitation/Correspondence/${payload.id}`,
    { message: payload.message },
  );
  return result.data.data;
};

export const respondToInvitationService = async (
  payload: RespondToInvitation,
) => {
  const result = await instance.put(
    `/Bookings/Invitation/Accept/${payload.id}?accept=${payload.accept}`,
  );
  return result.data;
};
